<script setup>
import { computed, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import conversationVisibilityScopesAPI from 'dashboard/api/conversationVisibilityScopes';

const agents = ref([]);
const teams = ref([]);
const scopesByUserId = ref({});
const isFetching = ref(false);
const savingByUserId = ref({});

const sortedAgents = computed(() =>
  agents.value.slice().sort((a, b) => displayName(a).localeCompare(displayName(b)))
);

const fetchScopes = async () => {
  try {
    isFetching.value = true;
    const { data } = await conversationVisibilityScopesAPI.get();
    agents.value = data.agents || [];
    teams.value = data.teams || [];
    scopesByUserId.value = Object.fromEntries(
      (data.scopes || []).map(scope => [
        Number(scope.user_id),
        {
          all: Boolean(scope.all),
          team_ids: (scope.team_ids || []).map(Number),
          managed_user_ids: (scope.managed_user_ids || []).map(Number),
        },
      ])
    );
  } catch (error) {
    useAlert('Não foi possível carregar os acessos de conversas.');
  } finally {
    isFetching.value = false;
  }
};

onMounted(fetchScopes);

const displayName = agent => agent.available_name || agent.name || agent.email;

const scopeFor = userId => {
  if (!scopesByUserId.value[userId]) {
    scopesByUserId.value[userId] = {
      all: false,
      team_ids: [],
      managed_user_ids: [],
    };
  }

  return scopesByUserId.value[userId];
};

const updateMultiSelect = (event, userId, key) => {
  scopeFor(userId)[key] = Array.from(event.target.selectedOptions).map(option =>
    Number(option.value)
  );
};

const managedAgentOptions = userId =>
  sortedAgents.value.filter(agent => agent.id !== userId);

const saveScope = async userId => {
  const scope = scopeFor(userId);
  try {
    savingByUserId.value[userId] = true;
    const { data } = await conversationVisibilityScopesAPI.updateForUser(userId, {
      all: scope.all,
      team_ids: scope.all ? [] : scope.team_ids,
      managed_user_ids: scope.all ? [] : scope.managed_user_ids,
    });

    scopesByUserId.value[userId] = {
      all: Boolean(data.all),
      team_ids: (data.team_ids || []).map(Number),
      managed_user_ids: (data.managed_user_ids || []).map(Number),
    };
    useAlert('Acessos de conversa atualizados.');
  } catch (error) {
    useAlert('Não foi possível salvar os acessos deste usuário.');
  } finally {
    savingByUserId.value[userId] = false;
  }
};
</script>

<template>
  <div class="flex-1 overflow-auto">
    <BaseSettingsHeader
      title="Acessos de conversas"
      description="Defina quais usuários podem ver todas as conversas, conversas de times ou conversas de usuários específicos."
    />

    <div class="mt-6 text-n-slate-11">
      <woot-loading-state
        v-if="isFetching"
        message="Carregando acessos de conversas..."
      />

      <div
        v-else
        class="overflow-x-auto border border-n-weak rounded-lg bg-n-solid-1"
      >
        <table class="min-w-full divide-y divide-n-weak">
          <thead>
            <tr class="text-left text-sm text-n-slate-11">
              <th class="p-4 font-semibold min-w-[220px]">Usuário</th>
              <th class="p-4 font-semibold w-[140px]">Vê tudo</th>
              <th class="p-4 font-semibold min-w-[220px]">Times gerenciados</th>
              <th class="p-4 font-semibold min-w-[260px]">Usuários gerenciados</th>
              <th class="p-4 font-semibold w-[120px] text-right">Ações</th>
            </tr>
          </thead>

          <tbody class="divide-y divide-n-weak">
            <tr v-for="agent in sortedAgents" :key="agent.id">
              <td class="p-4 align-top">
                <span class="block font-medium">{{ displayName(agent) }}</span>
                <span class="block text-sm text-n-slate-10">{{ agent.email }}</span>
                <span class="block mt-1 text-xs uppercase text-n-slate-9">
                  {{ agent.role }}
                </span>
              </td>

              <td class="p-4 align-top">
                <label class="inline-flex items-center gap-2">
                  <input
                    v-model="scopeFor(agent.id).all"
                    type="checkbox"
                    class="rounded border-n-weak"
                  />
                  <span class="text-sm">Diretor</span>
                </label>
              </td>

              <td class="p-4 align-top">
                <select
                  class="w-full min-h-[96px] rounded-lg border border-n-weak bg-n-solid-1 p-2 text-sm"
                  multiple
                  :disabled="scopeFor(agent.id).all"
                  :value="scopeFor(agent.id).team_ids"
                  @change="updateMultiSelect($event, agent.id, 'team_ids')"
                >
                  <option v-for="team in teams" :key="team.id" :value="team.id">
                    {{ team.name }}
                  </option>
                </select>
              </td>

              <td class="p-4 align-top">
                <select
                  class="w-full min-h-[96px] rounded-lg border border-n-weak bg-n-solid-1 p-2 text-sm"
                  multiple
                  :disabled="scopeFor(agent.id).all"
                  :value="scopeFor(agent.id).managed_user_ids"
                  @change="
                    updateMultiSelect($event, agent.id, 'managed_user_ids')
                  "
                >
                  <option
                    v-for="managedAgent in managedAgentOptions(agent.id)"
                    :key="managedAgent.id"
                    :value="managedAgent.id"
                  >
                    {{ displayName(managedAgent) }}
                  </option>
                </select>
              </td>

              <td class="p-4 align-top text-right">
                <Button
                  label="Salvar"
                  icon="i-lucide-save"
                  xs
                  :is-loading="savingByUserId[agent.id]"
                  @click="saveScope(agent.id)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p class="mt-4 text-sm text-n-slate-10">
        Usuários sem escopo adicional veem apenas conversas atribuídas a eles ou
        conversas em que participam.
      </p>
    </div>
  </div>
</template>
